import type { Tip, TranscriptSegmentDTO } from '@copilot/shared';
import { escapeRegExp } from './text';

/**
 * Pure logic behind the transcript viewer: merging tips into the transcript
 * timeline and finding/highlighting search matches.
 */

export type TimelineItem =
  | { kind: 'segment'; key: string; atMs: number; segment: TranscriptSegmentDTO }
  | { kind: 'tip'; key: string; atMs: number; tip: Tip };

/**
 * Merge transcript segments and tips into one list ordered by time.
 * A tip is placed after every segment that started at or before its
 * `callOffsetMs` (tips are produced in response to what was just said), so
 * on a tie the segment comes first. Input order does not matter.
 */
export function buildTimeline(segments: readonly TranscriptSegmentDTO[], tips: readonly Tip[]): TimelineItem[] {
  const segs = [...segments].sort((a, b) => a.startMs - b.startMs);
  const sortedTips = [...tips].sort((a, b) => a.callOffsetMs - b.callOffsetMs);
  const out: TimelineItem[] = [];
  let t = 0;
  for (const segment of segs) {
    while (t < sortedTips.length && sortedTips[t]!.callOffsetMs < segment.startMs) {
      const tip = sortedTips[t++]!;
      out.push({ kind: 'tip', key: `tip-${tip.id}`, atMs: tip.callOffsetMs, tip });
    }
    out.push({ kind: 'segment', key: `seg-${segment.id}`, atMs: segment.startMs, segment });
  }
  while (t < sortedTips.length) {
    const tip = sortedTips[t++]!;
    out.push({ kind: 'tip', key: `tip-${tip.id}`, atMs: tip.callOffsetMs, tip });
  }
  return out;
}

export interface TextPart {
  text: string;
  /** Global index of this match across the whole transcript; undefined for non-matching text. */
  matchIndex?: number;
}

function queryRegExp(query: string): RegExp | null {
  const q = query.trim();
  return q ? new RegExp(escapeRegExp(q), 'gi') : null;
}

/** Number of case-insensitive occurrences of `query` in `text`. */
export function countMatches(text: string, query: string): number {
  const re = queryRegExp(query);
  if (!re) return 0;
  return text.match(re)?.length ?? 0;
}

/**
 * Split `text` into plain and matching parts. Matches are numbered starting at
 * `firstIndex`, so a caller can number matches across many segments.
 */
export function splitMatches(text: string, query: string, firstIndex = 0): TextPart[] {
  const re = queryRegExp(query);
  if (!re) return [{ text }];
  const parts: TextPart[] = [];
  let last = 0;
  let idx = firstIndex;
  for (const m of text.matchAll(re)) {
    const start = m.index;
    if (start > last) parts.push({ text: text.slice(last, start) });
    parts.push({ text: m[0], matchIndex: idx++ });
    last = start + m[0].length;
  }
  if (last < text.length) parts.push({ text: text.slice(last) });
  return parts.length ? parts : [{ text }];
}

export interface MatchIndex {
  total: number;
  /** Index of the first match inside each segment that has matches. */
  firstIndexBySegment: Map<string, number>;
}

/** Number every match across the segments (in timeline order) so the viewer can jump between them. */
export function indexMatches(items: readonly TimelineItem[], query: string): MatchIndex {
  const firstIndexBySegment = new Map<string, number>();
  let total = 0;
  if (!query.trim()) return { total, firstIndexBySegment };
  for (const item of items) {
    if (item.kind !== 'segment') continue;
    const n = countMatches(item.segment.text, query);
    if (n > 0) {
      firstIndexBySegment.set(item.segment.id, total);
      total += n;
    }
  }
  return { total, firstIndexBySegment };
}

/** Step to the next/previous match, wrapping around. Returns -1 when there are none. */
export function stepMatch(current: number, total: number, delta: 1 | -1): number {
  if (total <= 0) return -1;
  if (current < 0) return delta === 1 ? 0 : total - 1;
  return (((current + delta) % total) + total) % total;
}
